/**
 * The Appointments page's New booking, Change and Cancel: which branch and
 * services are offered, how far Upcoming reads, and the reason a cancel carries.
 */
import { addDays } from './bookingStatus'
import type { StaffKey } from './staffText'
import type { BookingChannel, BookingFormFields, CatalogueEntry, FieldMode, FinMode } from './types'

/** Whether New booking opens with a Branch step: the person works several
 *  branches, or every branch of the organisation (no pins). */
export function choosesBranch(reach: number, orgWide: boolean): boolean {
  return reach > 1 || orgWide
}

/** The branch New booking starts on: the one in view when it takes bookings, else the first that does. */
export function startBranch(catalogue: readonly CatalogueEntry[], inView: string): string {
  if (catalogue.some((e) => e.branch.id === inView)) return inView
  return catalogue[0]?.branch.id ?? ''
}

export function entryOf(catalogue: readonly CatalogueEntry[] | null | undefined, branchId: string): CatalogueEntry | undefined {
  return catalogue?.find((e) => e.branch.id === branchId)
}

/** A service chosen at one branch kept on a change of branch only when the new
 *  one also books it; a branch booking one service has it chosen. */
export function keptService(entry: CatalogueEntry | undefined, serviceId: string): string {
  if (entry?.services.some((s) => s.id === serviceId)) return serviceId
  return entry?.services.length === 1 ? entry.services[0]!.id : ''
}

/** Opens the slot flow; without online booking or a branch to book at, the
 *  hand-made booking (reference, day, time) is all there is. */
export function slotFlow(bookOnline: boolean, catalogue: readonly CatalogueEntry[] | null): boolean {
  return bookOnline && (catalogue === null || catalogue.length > 0)
}

/** The server's most days in one bookings range. */
export const MAX_RANGE_DAYS = 31

/** The days after today Upcoming shows, as ranges of at most MAX_RANGE_DAYS: one request each. */
export function upcomingRanges(today: string, days: number, max = MAX_RANGE_DAYS): { from: string; to: string }[] {
  const out: { from: string; to: string }[] = []
  for (let start = 1; start <= days; start += max) {
    const end = Math.min(days, start + max - 1)
    out.push({ from: addDays(today, start), to: addDays(today, end) })
  }
  return out
}

/** Days Upcoming must read to reach day, in whole steps; at least current. */
export function daysToReach(today: string, day: string, current: number, step: number): number {
  const ahead = Math.round((Date.parse(`${day}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / 86_400_000)
  if (ahead <= current) return current
  return Math.ceil(ahead / step) * step
}

export const CANCEL_REASONS = ['asked', 'duplicate', 'closed', 'other'] as const
export type CancelReason = (typeof CANCEL_REASONS)[number]

export const CANCEL_REASON_KEY: Record<CancelReason, StaffKey> = {
  asked: 'appointments.why.asked',
  duplicate: 'appointments.why.duplicate',
  closed: 'appointments.why.closed',
  other: 'appointments.why.other',
}

/** The server's longest reason, in characters. */
export const MAX_CANCEL_REASON = 200

/** The one line a cancel sends: the chosen reason's words, or what was typed for Other. */
export function cancelReasonText(choice: CancelReason, typed: string, label: (c: CancelReason) => string): string {
  const own = typed.replace(/\s+/g, ' ').trim()
  const text = choice === 'other' && own ? own : label(choice)
  return Array.from(text).slice(0, MAX_CANCEL_REASON).join('')
}

export interface FormRules {
  finMode: FinMode
  channels: BookingChannel[]
  fields: BookingFormFields
}

/** What the New booking form asks, from GET …/booking/form or the slots answer
 *  (which carries the same). Without fields it asks what it always did. */
export function formRules(answer: { finMode: FinMode; channels: BookingChannel[]; fields?: BookingFormFields }): FormRules {
  const f = answer.fields
  return {
    finMode: answer.finMode,
    channels: answer.channels,
    fields: {
      name: f?.name ?? 'required',
      mobile: f?.mobile ?? 'off',
      email: f?.email ?? 'off',
      fin: f?.fin ?? answer.finMode,
      extra: f?.extra ?? [],
    },
  }
}

/** The mode of the contact the code does not go to: the e-mail beside SMS, the mobile beside e-mail. */
export function otherMode(fields: Pick<BookingFormFields, 'mobile' | 'email'>, channel: BookingChannel): FieldMode {
  return channel === 'sms' ? fields.email : fields.mobile
}
